import React from 'react';

interface ExerciseCategorySelectorProps {
  onCategorySelect: (category: string) => void;
  className?: string;
}

// Определяем категории мышц с иконками и русскими названиями
const MUSCLE_CATEGORIES = [
  {
    id: 'грудь',
    name: 'Грудь',
    icon: '💪',
    description: 'Грудные мышцы',
    color: 'bg-red-100 text-red-800 hover:bg-red-200'
  },
  {
    id: 'спина',
    name: 'Спина',
    icon: '🔙',
    description: 'Мышцы спины',
    color: 'bg-blue-100 text-blue-800 hover:bg-blue-200'
  },
  {
    id: 'ноги',
    name: 'Ноги',
    icon: '🦵',
    description: 'Мышцы ног',
    color: 'bg-green-100 text-green-800 hover:bg-green-200'
  },
  {
    id: 'ягодицы',
    name: 'Ягодицы',
    icon: '🍑',
    description: 'Ягодичные мышцы',
    color: 'bg-pink-100 text-pink-800 hover:bg-pink-200'
  },
  {
    id: 'плечи',
    name: 'Плечи',
    icon: '💪',
    description: 'Дельтовидные мышцы',
    color: 'bg-orange-100 text-orange-800 hover:bg-orange-200'
  },
  {
    id: 'бицепс',
    name: 'Бицепс',
    icon: '💪',
    description: 'Двуглавая мышца плеча',
    color: 'bg-purple-100 text-purple-800 hover:bg-purple-200'
  },
  {
    id: 'трицепс',
    name: 'Трицепс',
    icon: '💪',
    description: 'Трёхглавая мышца плеча',
    color: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
  },
  {
    id: 'предплечья',
    name: 'Предплечья',
    icon: '💪',
    description: 'Мышцы предплечий',
    color: 'bg-gray-100 text-gray-800 hover:bg-gray-200'
  },
  {
    id: 'кардио',
    name: 'Кардио',
    icon: '❤️',
    description: 'Кардио упражнения',
    color: 'bg-red-100 text-red-800 hover:bg-red-200'
  },
  {
    id: 'растяжка',
    name: 'Растяжка',
    icon: '🧘',
    description: 'Упражнения на растяжку',
    color: 'bg-teal-100 text-teal-800 hover:bg-teal-200'
  },
  {
    id: 'функциональные',
    name: 'Функциональные',
    icon: '🏃',
    description: 'Функциональные тренировки',
    color: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
  }
];

/**
 * ExerciseCategorySelector component for selecting muscle group categories
 * Displays categories as cards with icons and descriptions
 */
export const ExerciseCategorySelector: React.FC<ExerciseCategorySelectorProps> = ({
  onCategorySelect,
  className = ''
}) => {
  const handleCategoryClick = (categoryId: string) => {
    onCategorySelect(categoryId);
  };

  return (
    <div className={`exercise-category-selector ${className}`}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Выберите группу мышц</h2>
        <p className="text-gray-600">Нажмите на категорию, чтобы посмотреть упражнения</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MUSCLE_CATEGORIES.map(category => (
          <button
            key={category.id}
            onClick={() => handleCategoryClick(category.id)}
            className={`
              p-6 rounded-lg border-2 border-transparent transition-all duration-200
              ${category.color}
              hover:scale-105 hover:shadow-md
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              text-left
            `}
          >
            <div className="flex items-center mb-3">
              <span className="text-3xl mr-3">{category.icon}</span>
              <h3 className="text-lg font-semibold">{category.name}</h3>
            </div>
            <p className="text-sm opacity-80">{category.description}</p>
          </button>
        ))}
      </div>

      {/* Кнопка "Все упражнения" */}
      <div className="mt-6">
        <button
          onClick={() => handleCategoryClick('')}
          className="w-full p-4 rounded-lg border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <div className="flex items-center justify-center">
            <span className="text-2xl mr-3">📋</span>
            <span className="text-lg font-semibold">Все упражнения</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default ExerciseCategorySelector;